import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Search, Home, Clock, CalendarDays } from 'lucide-react';
import { estimateWorkoutSeconds, formatDuration } from '../../utils/workoutDuration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AssignFlexibleModal — unified "Assign template" modal.
 * After picking a client, the trainer chooses to assign the template to an
 * existing upcoming booking OR as a "Rutina libre" (free routine).
 *
 * Props:
 *   - template: the template object (id, name) being assigned
 *   - onClose: () => void
 *   - onAssigned: (workoutId) => void   (optional, for refresh hooks)
 */
const AssignFlexibleModal = ({ template, onClose, onAssigned }) => {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [mode, setMode] = useState('booking'); // 'booking' | 'flexible'
  const [bookings, setBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [availableFrom, setAvailableFrom] = useState('');
  const [availableUntil, setAvailableUntil] = useState('');
  const [trainerNotes, setTrainerNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const authCfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [clientsRes, bookingsRes] = await Promise.all([
          axios.get(`${API}/users/clients`, authCfg),
          axios.get(`${API}/trainer/future-bookings`, authCfg),
        ]);
        if (!cancelled) {
          setClients((clientsRes.data || []).filter((u) => u.role === 'client'));
          setBookings(bookingsRes.data || []);
        }
      } catch (e) {
        if (!cancelled) setError('Could not load client list');
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Reset the chosen booking whenever the client changes.
  useEffect(() => { setSelectedBookingId(null); }, [selectedClientId]);

  const clientBookings = useMemo(
    () => bookings.filter((b) => b.client_id === selectedClientId),
    [bookings, selectedClientId]
  );

  const filteredClients = useMemo(() => {
    if (!clientSearchTerm.trim()) return clients;
    const q = clientSearchTerm.trim().toLowerCase();
    return clients.filter((c) =>
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [clients, clientSearchTerm]);

  const dateError = useMemo(() => {
    if (availableFrom && availableUntil && availableUntil < availableFrom) {
      return 'La fecha "hasta" debe ser igual o posterior a "desde"';
    }
    return '';
  }, [availableFrom, availableUntil]);

  const canSubmit = selectedClientId && !dateError && !submitting &&
    (mode === 'flexible' || (mode === 'booking' && selectedBookingId));

  const handleAssign = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      if (mode === 'booking') {
        await axios.post(`${API}/trainer/assign-template`, {
          booking_id: selectedBookingId,
          template_id: template.id,
        }, authCfg);
        onAssigned?.(selectedBookingId);
      } else {
        const res = await axios.post(`${API}/trainer/assign-template-flexible`, {
          client_id: selectedClientId,
          template_id: template.id,
          available_from: availableFrom || null,
          available_until: availableUntil || null,
          trainer_notes: trainerNotes || null,
        }, authCfg);
        onAssigned?.(res.data?.workout_id);
      }
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error assigning template');
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/70 z-[9999] overflow-y-auto"
      onClick={onClose}
      data-testid="assign-flexible-modal"
    >
      <div className="min-h-screen flex items-start sm:items-center justify-center p-4">
        <div
          className="bg-blue-900/95 rounded-xl max-w-lg w-full my-4 sm:my-8 max-h-[85vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-amber-400/40 flex justify-between items-center bg-amber-900/30">
            <div>
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-amber-300" />
                Asignar plantilla
              </h3>
              <p className="text-amber-200 text-sm mt-0.5">
                {template?.name || template?.template_name || 'Template'}
              </p>
              <p className="text-amber-300 text-xs mt-1 flex items-center gap-1" data-testid="assign-flexible-est-duration">
                <Clock className="w-3 h-3" /> Duración estimada: ~{formatDuration(estimateWorkoutSeconds(template?.exercises || []))}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-blue-300 hover:text-white text-xl"
              data-testid="assign-flexible-close-btn"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="p-4 overflow-y-auto flex-1">
            {/* Client picker */}
            <div className="mb-4">
              <label className="block text-blue-200 text-sm font-medium mb-1">
                Cliente <span className="text-red-400">*</span>
              </label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  placeholder="Buscar cliente..."
                  className="input-field w-full pl-10"
                  data-testid="assign-flexible-client-search"
                />
              </div>

              {loadingClients ? (
                <p className="text-blue-300 text-sm py-2">Cargando clientes...</p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto rounded-lg bg-blue-950/40 p-1">
                  {filteredClients.length === 0 ? (
                    <p className="text-blue-300 text-sm py-2 px-2">Sin resultados</p>
                  ) : (
                    filteredClients.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setSelectedClientId(c.id)}
                        className={`w-full text-left px-3 py-2 rounded transition ${
                          selectedClientId === c.id
                            ? 'bg-amber-500/30 text-white ring-1 ring-amber-300'
                            : 'text-blue-100 hover:bg-blue-800/40'
                        }`}
                        data-testid={`assign-flexible-client-${c.id}`}
                      >
                        <div className="text-sm font-medium">{c.full_name}</div>
                        <div className="text-xs text-blue-300">{c.email}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Mode + destination — shown once a client is picked */}
            {selectedClientId && (
              <>
                <div className="flex gap-2 mb-3" data-testid="assign-mode-toggle">
                  <button
                    onClick={() => setMode('booking')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition border ${
                      mode === 'booking'
                        ? 'bg-blue-500/30 text-white border-blue-400'
                        : 'bg-blue-950/40 text-blue-200 border-transparent hover:bg-blue-800/40'
                    }`}
                    data-testid="assign-mode-booking"
                  >
                    <CalendarDays className="w-4 h-4" /> A una cita
                  </button>
                  <button
                    onClick={() => setMode('flexible')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition border ${
                      mode === 'flexible'
                        ? 'bg-amber-500/30 text-white border-amber-400'
                        : 'bg-blue-950/40 text-blue-200 border-transparent hover:bg-blue-800/40'
                    }`}
                    data-testid="assign-mode-flexible"
                  >
                    <Home className="w-4 h-4" /> Rutina libre
                  </button>
                </div>

                {/* BOOKING MODE — pick an upcoming booking for this client */}
                {mode === 'booking' && (
                  <div className="mb-4" data-testid="assign-booking-listbox">
                    <label className="block text-blue-200 text-sm font-medium mb-1">
                      Cita próxima <span className="text-red-400">*</span>
                    </label>
                    {clientBookings.length === 0 ? (
                      <p className="text-blue-300 text-sm bg-blue-950/40 rounded-lg p-3">
                        Este cliente no tiene citas próximas. Usa <strong>Rutina libre</strong> o crea una cita en el calendario.
                      </p>
                    ) : (
                      <div className="space-y-1 max-h-48 overflow-y-auto rounded-lg bg-blue-950/40 p-1">
                        {clientBookings.map((b) => (
                          <button
                            key={b.id}
                            onClick={() => setSelectedBookingId(b.id)}
                            className={`w-full text-left px-3 py-2 rounded transition ${
                              selectedBookingId === b.id
                                ? 'bg-blue-500/30 text-white ring-1 ring-blue-300'
                                : 'text-blue-100 hover:bg-blue-800/40'
                            }`}
                            data-testid={`assign-booking-${b.id}`}
                          >
                            <div className="text-sm font-medium">
                              {b.date} · {b.start_time}–{b.end_time}
                            </div>
                            {b.has_workout && (
                              <div className="text-xs text-amber-300">
                                ⚠ Ya tiene una rutina ({b.workout_exercises_count} ej.) — se reemplazará
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* FLEXIBLE MODE — free routine, no calendar slot */}
                {mode === 'flexible' && (
                  <>
                    <p className="text-amber-200/90 text-sm bg-amber-500/10 border border-amber-400/30 rounded-lg p-3 mb-4">
                      <strong>🏠 Rutina libre</strong> — sin cita en el calendario. El cliente
                      la ejecuta cuando quiera (fin de semana, viaje, casa). Puede imprimirla.
                    </p>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <label className="block text-blue-200 text-xs font-medium mb-1">
                          Disponible desde <span className="text-blue-400/60">(opcional)</span>
                        </label>
                        <input
                          type="date"
                          value={availableFrom}
                          onChange={(e) => setAvailableFrom(e.target.value)}
                          className="input-field w-full text-sm"
                          data-testid="assign-flexible-from"
                        />
                      </div>
                      <div>
                        <label className="block text-blue-200 text-xs font-medium mb-1">
                          Hasta <span className="text-blue-400/60">(opcional)</span>
                        </label>
                        <input
                          type="date"
                          value={availableUntil}
                          onChange={(e) => setAvailableUntil(e.target.value)}
                          className="input-field w-full text-sm"
                          data-testid="assign-flexible-until"
                        />
                      </div>
                    </div>
                    {dateError && (
                      <p className="text-red-300 text-xs -mt-2 mb-3">{dateError}</p>
                    )}
                    <div className="mb-4">
                      <label className="block text-blue-200 text-xs font-medium mb-1">
                        Notas para el cliente <span className="text-blue-400/60">(opcional)</span>
                      </label>
                      <textarea
                        value={trainerNotes}
                        onChange={(e) => setTrainerNotes(e.target.value)}
                        rows={3}
                        className="input-field w-full text-sm"
                        placeholder="Ej: Hazla el sábado por la mañana, descansa 60–90s entre series..."
                        data-testid="assign-flexible-notes"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {error && (
              <p className="text-red-300 text-sm bg-red-500/10 border border-red-400/30 rounded p-2 mb-2">
                {error}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-blue-400/30 flex gap-2">
            <button
              onClick={onClose}
              className="btn-secondary flex-1"
              data-testid="assign-flexible-cancel-btn"
            >
              Cancelar
            </button>
            <button
              onClick={handleAssign}
              disabled={!canSubmit}
              className={`flex-1 px-4 py-2 rounded-lg font-semibold transition ${
                canSubmit
                  ? 'bg-amber-500 hover:bg-amber-400 text-black'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="assign-flexible-submit-btn"
            >
              {submitting
                ? 'Asignando...'
                : mode === 'booking' ? '📅 Asignar a la cita' : '🏠 Asignar Rutina libre'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssignFlexibleModal;
